import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Input,
  Button,
  Upload,
  Modal,
  message as antdMessage,
  Avatar,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  UploadOutlined,
  CloseCircleOutlined,
  FileOutlined,
  SendOutlined,
  UserOutlined,
  CloseOutlined,
  PaperClipOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import './chat.css';
import { ref as dbRef, push, update, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc } from 'firebase/firestore';
import useChatMessages from '../../hooks/useChatMessages';
import { useFirebase } from '../../FirebaseContext';

const { TextArea } = Input;
const { Text } = Typography;

// ----------  ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ  ---------- //
function truncateFileName(fileName, maxLength = 30) {
  if (fileName.length <= maxLength) return fileName;
  const extIndex = fileName.lastIndexOf('.');
  const extension = extIndex > -1 ? fileName.slice(extIndex) : '';
  const baseName = extIndex > -1 ? fileName.slice(0, extIndex) : fileName;
  return `${baseName.slice(0, maxLength - extension.length - 3)}...${extension}`;
}

function extractFileName(url) {
  try {
    const fullPath = decodeURIComponent(url.split('?')[0]);
    return fullPath.substring(fullPath.lastIndexOf('/') + 1);
  } catch {
    return 'File';
  }
}

function formatDateSeparator(date) {
  const now = new Date();
  const d = new Date(date);
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear();

  if (isToday) return 'Dzisiaj';
  if (isYesterday) return 'Wczoraj';

  return d.toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatMessageTime(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}

// ----------  ОСНОВНОЙ КОМПОНЕНТ  ---------- //
const ChatWindow = ({ selectedChat, user, userInfo }) => {
  // -------------------- state -------------------- //
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  // -------------------- refs -------------------- //
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  // -------------------- firebase из контекста -------------------- //
  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database;
  const storage = currentFirebase.storage;
  const firestore = currentFirebase.firestore;

  // Отслеживаем смену проекта
  const prevFirebase = useRef(currentFirebase);
  useEffect(() => {
    if (prevFirebase.current !== currentFirebase) {
      antdMessage.info('Przełączam projekt...', 2);
      prevFirebase.current = currentFirebase;
    }
  }, [currentFirebase]);

  // -------------------- сообщения -------------------- //
  const messages = useChatMessages(selectedChat?.id);
  const sortedMessages = useMemo(
    () => (messages ? [...messages].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)) : []),
    [messages]
  );

  // проскроллить вниз при появлении новых сообщений — только внутри контейнера
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [sortedMessages]);

  // фокус на input при открытии чата
  useEffect(() => {
    if (inputRef.current && selectedChat?.status !== 'closed') {
      inputRef.current.focus();
    }
  }, [selectedChat]);

  // отмечаем как прочитанные
  useEffect(() => {
    if (!selectedChat || !sortedMessages.length) return;

    const updatePaths = {};
    sortedMessages.forEach((msg) => {
      if (!msg.isRead && msg.senderRole === 'user') {
        updatePaths[`chats/${selectedChat.id}/messages/${msg.id}/isRead`] = true;
        updatePaths[`chats/${selectedChat.id}/messages/${msg.id}/readAt`] = new Date().toISOString();
      }
    });
    if (
      selectedChat.lastMessage?.senderRole === 'user' &&
      selectedChat.lastMessage?.isRead === false
    ) {
      updatePaths[`chats/${selectedChat.id}/lastMessage/isRead`] = true;
      updatePaths[`chats/${selectedChat.id}/lastMessage/readAt`] = new Date().toISOString();
    }
    if (Object.keys(updatePaths).length) update(dbRef(db), updatePaths);
  }, [selectedChat, sortedMessages, db]);

  // -------------------- отображение сообщений -------------------- //
  const formattedMessages = useMemo(() => {
    if (!selectedChat) return null;

    const result = [];
    let lastDate = null;
    let lastSenderId = null;

    sortedMessages.forEach((msg, index) => {
      const isOwnMessage = msg.senderRole === 'admin';
      const senderName = isOwnMessage ? 'Ty' : msg.sender;
      const msgDateObj = new Date(msg.timestamp);
      const msgDateString = msgDateObj.toDateString();

      if (msgDateString !== lastDate) {
        result.push(
          <div key={`date-${msg.timestamp}`} className="date-separator">
            <span className="date-separator-text">{formatDateSeparator(msg.timestamp)}</span>
          </div>
        );
        lastDate = msgDateString;
        lastSenderId = null;
      }

      const showSenderName = msg.senderId !== lastSenderId && !isOwnMessage;
      result.push(
        <div key={`${msg.timestamp}-${index}`} className={`message-container ${isOwnMessage ? 'own' : 'other'}`}>
          {!isOwnMessage && (
            <Avatar size={32} icon={<UserOutlined />} className="message-avatar" />
          )}

          <div className="message-wrapper">
            {showSenderName && (
              <div className="message-sender-name">{senderName}</div>
            )}

            <div className={`message-bubble ${isOwnMessage ? 'own' : 'other'}`}>
              {msg.text && <div className="message-text">{msg.text}</div>}

              {/* image */}
              {msg.fileUrl && msg.fileType?.startsWith('image/') && (
                <div className="message-image-container">
                  <img
                    src={msg.fileUrl}
                    alt="img"
                    className="message-image"
                    onClick={() => window.open(msg.fileUrl, '_blank')}
                  />
                </div>
              )}

              {/* file */}
              {msg.fileUrl && msg.fileType && !msg.fileType.startsWith('image/') && (
                <a
                  href={msg.fileUrl}
                  download
                  className="message-file"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FileOutlined className="message-file-icon" />
                  <span className="message-file-name">
                    {truncateFileName(extractFileName(msg.fileUrl))}
                  </span>
                </a>
              )}

              <div className="message-meta">
                <span className="message-time">{formatMessageTime(msg.timestamp)}</span>
                {isOwnMessage && (
                  <span className={`message-status ${msg.isRead ? 'read' : 'sent'}`}>
                    {msg.isRead ? <CheckOutlined /> : <CheckOutlined />}
                    {msg.isRead && <CheckOutlined />}
                  </span>
                )}
              </div>
            </div>
          </div>

          {isOwnMessage && (
            <Avatar size={32} className="message-avatar">
              {userInfo?.firstName?.[0] || 'A'}
            </Avatar>
          )}
        </div>
      );
      lastSenderId = msg.senderId;
    });

    return result;
  }, [sortedMessages, selectedChat, userInfo]);

  // -------------------- file helpers -------------------- //
  const onFileChangeLocal = (info) => {
    const file = info.file.originFileObj || info.file;
    if (!file) return;
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      setPreviewImageUrl(URL.createObjectURL(file));
    } else {
      setPreviewImageUrl(null);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setPreviewImageUrl(null);
  };

  // -------------------- отправка сообщения -------------------- //
  const handleSendMessage = async () => {
    if (!selectedChat || !user) return;
    if (!message.trim() && !selectedFile) return;

    try {
      const senderName = 'Administrator';
      const senderRole = 'admin';

      let fileUrl = null;
      let fileType = null;

      if (selectedFile) {
        const fileRef = storageRef(storage, `chatFiles/${selectedChat.id}/${Date.now()}_${selectedFile.name}`);
        await uploadBytes(fileRef, selectedFile);
        fileUrl = await getDownloadURL(fileRef);
        fileType = selectedFile.type;
      }

      const newMsg = {
        text: message,
        fileUrl: fileUrl || null,
        fileType: fileType || null,
        sender: senderName,
        senderId: user.uid,
        senderRole,
        timestamp: new Date().toISOString(),
        isRead: false,
        readAt: null,
      };

      await push(dbRef(db, `chats/${selectedChat.id}/messages`), newMsg);
      await set(dbRef(db, `chats/${selectedChat.id}/lastMessage`), {
        text: message,
        fileUrl: fileUrl || null,
        fileType: fileType || null,
        senderName,
        senderRole,
        timestamp: newMsg.timestamp,
        isRead: false,
      });

      if (firestore) {
        await addDoc(collection(firestore, `users/${selectedChat.creatorId}/notifications`), {
          title: 'Nowa wiadomość w czacie',
          text: `Otrzymano nową wiadomość od ${senderName}: ${message}`,
          createdAt: new Date(),
          isRead: false,
        });
      }

      setMessage('');
      setSelectedFile(null);
      setPreviewImageUrl(null);
    } catch (err) {
      console.error('Ошибка при отправке сообщения:', err);
      antdMessage.error('Nie udało się wysłać wiadomości');
    }
  };

  // -------------------- закрытие чата -------------------- //
  const openCloseModal = () => setIsCloseModalVisible(true);
  const onCloseModal = () => setIsCloseModalVisible(false);

  const onCloseChat = async () => {
    try {
      const closedAt = new Date().toISOString();

      await update(dbRef(db, `chats/${selectedChat.id}`), {
        status: 'closed',
        closeReason,
        closedAt,
      });

      antdMessage.success('Czat został zamknięty.');
      setCloseReason('');
    } catch (err) {
      antdMessage.error('Nie udało się zamknąć czatu.');
    } finally {
      setIsCloseModalVisible(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ----------  RENDER  ---------- //
  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-window-header">
        <div className="chat-header-info">
          <Avatar size={48} icon={<UserOutlined />} className="chat-header-avatar" />
          <div className="chat-header-details">
            <div className="chat-header-name">
              {selectedChat?.chatName || 'Chat'}
            </div>
            <div className="chat-header-subtitle">
              <UserOutlined style={{ fontSize: '12px', marginRight: '4px' }} />
              {selectedChat?.creatorName || 'Nieznany'}
            </div>
          </div>
        </div>

        <div className="chat-header-actions">
          {selectedChat?.status === 'closed' ? (
            <Tag icon={<CloseCircleOutlined />} color="default">
              Zamknięty
            </Tag>
          ) : (
            <Tooltip title="Zamknij czat">
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={openCloseModal}
              >
                Zamknij
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages-container" ref={messagesContainerRef}>
        {formattedMessages}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {selectedChat?.status === 'closed' ? (
        <div className="chat-closed-notice">
          <CloseCircleOutlined style={{ fontSize: '24px', marginBottom: '8px', color: '#8c8c8c' }} />
          <Text strong>Czat został zamknięty</Text>
          {selectedChat?.closeReason && (
            <Text type="secondary" style={{ marginTop: '4px' }}>
              Powód: {selectedChat.closeReason}
            </Text>
          )}
          {selectedChat?.closedAt && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {new Date(selectedChat.closedAt).toLocaleString('pl-PL')}
            </Text>
          )}
        </div>
      ) : (
        <div className="chat-input-area">
          {/* File Preview */}
          {selectedFile && (
            <div className="file-preview">
              {previewImageUrl ? (
                <div className="file-preview-image">
                  <img src={previewImageUrl} alt="Preview" />
                  <Button
                    type="text"
                    danger
                    icon={<CloseOutlined />}
                    onClick={removeSelectedFile}
                    className="file-preview-remove"
                  />
                </div>
              ) : (
                <div className="file-preview-file">
                  <FileOutlined className="file-preview-icon" />
                  <Text className="file-preview-name">{truncateFileName(selectedFile.name)}</Text>
                  <Button
                    type="text"
                    danger
                    icon={<CloseOutlined />}
                    onClick={removeSelectedFile}
                  />
                </div>
              )}
            </div>
          )}

          {/* Input */}
          <div className="chat-input-wrapper">
            <Input.TextArea
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Napisz wiadomość..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              className="chat-input"
            />

            <div className="chat-input-actions">
              <Upload
                beforeUpload={() => false}
                onChange={onFileChangeLocal}
                showUploadList={false}
              >
                <Tooltip title="Załącz plik">
                  <Button
                    type="text"
                    icon={<PaperClipOutlined />}
                    className="chat-action-button"
                  />
                </Tooltip>
              </Upload>

              <Tooltip title="Wyślij (Enter)">
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  disabled={!message.trim() && !selectedFile}
                  className="chat-send-button"
                >
                  Wyślij
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      {/* Modal zamknięcia */}
      <Modal
        title="Zamknij czat"
        open={isCloseModalVisible}
        onOk={onCloseChat}
        onCancel={onCloseModal}
        okText="Zamknij"
        cancelText="Anuluj"
        okButtonProps={{ danger: true }}
      >
        <p>Podaj powód zamknięcia (opcjonalnie):</p>
        <TextArea
          rows={3}
          value={closeReason}
          onChange={(e) => setCloseReason(e.target.value)}
          placeholder="Np. problem rozwiązany, duplikat zgłoszenia..."
        />
      </Modal>
    </div>
  );
};

export default ChatWindow;
